/**
 * The measuring panel above the queue.
 *
 * Checking six hundred rows is the work of weeks and is about trust. Checking
 * twenty is the work of an hour and is about knowing whether this corpus is
 * roughly right or roughly wrong — which is the thing nothing built on it can
 * be sized without, and which no amount of software can establish, because
 * every automated check here has itself been wrong at least once.
 *
 * The rate is stated as an accept rate rather than an accuracy, because that
 * is what it is: one representative's judgement, not ground truth. And it is
 * null rather than zero until something has been read, since "0% wrong" from
 * nought reviews is the opposite of what no data means.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export function Sample({
  sampleOnly,
  onSampleOnly,
}: {
  sampleOnly: boolean;
  onSampleOnly: (on: boolean) => void;
}) {
  const sample = useQuery({ queryKey: ['sample'], queryFn: api.sample });
  const data = sample.data;
  if (!data) return null;

  const { measured } = data;
  const done = data.items.length === 0;

  return (
    /* The one panel on this screen that is not a queue row, so it takes the
       subtle ground §7 names for exactly that job — the card vocabulary alone
       would make it the first item in the list rather than the frame around
       it. */
    <section className="card mb-6 border-[var(--color-rule-strong)] bg-[var(--color-subtle)]">
      <h2 className="card-header text-[13px] font-semibold tracking-tight">
        Measure before you trust
      </h2>
      <p className="measure mb-2 text-[13px] leading-relaxed text-[var(--color-muted)]">
        Twenty rows, spread across the kinds that fail differently. Reading them gives an error rate
        for the whole corpus — enough to tell a few per cent from a third, which is the decision
        this is for. It is not enough to certify anything.
      </p>

      {/* The strata are figures, and a figure is tabular mono per §7. */}
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1">
        {data.strata.map((stratum) => (
          <span key={stratum.key} className="label" title={stratum.why}>
            {stratum.label}:{' '}
            <span className="tabular-nums">{stratum.got}</span>
          </span>
        ))}
      </div>

      {/*
        The toggle, because showing what a representative sample contains while
        leaving the reviewer to work the ordinary queue measures nothing. That
        queue is ordered by consequence — comparator_better first — so ten
        reviews in, every one of them was that verdict and the 135 silences
        behind the Protect board were still unmeasured.
      */}
      <button
        type="button"
        onClick={() => onSampleOnly(!sampleOnly)}
        aria-pressed={sampleOnly}
        className="hit mb-2.5 rounded-[6px] border border-[var(--color-ink)] px-3 py-1.5 text-[12.5px]"
      >
        {sampleOnly ? '← Back to the whole queue' : 'Review these twenty →'}
      </button>

      <p className="measure text-[12.5px] leading-snug">
        {measured.errorRate === null ? (
          <span className="text-[var(--color-defect)]">
            Nothing has been read yet, so there is no rate — which is not the same as no errors.
          </span>
        ) : (
          <>
            {/* The rate is the reason the panel exists, so it is set as a
                figure — mono, tabular — and not as bold prose. */}
            <span className="font-mono tabular-nums">{measured.wrong}</span> of{' '}
            <span className="font-mono tabular-nums">{measured.reviewed}</span> reviewed rows were
            corrected or rejected —{' '}
            <strong
              className="font-mono text-[14px] tabular-nums"
              style={{
                color:
                  measured.errorRate > 0.15 ? 'var(--color-defect)' : 'var(--color-win)',
              }}
            >
              {Math.round(measured.errorRate * 100)}%
            </strong>
            .{' '}
            {measured.reviewed < 20 && (
              <span className="text-[var(--color-muted)]">
                On {measured.reviewed} rows, so treat it as a signal rather than a measurement.
              </span>
            )}
          </>
        )}
      </p>

      {done && (
        <p className="mt-1 text-[12px] text-[var(--color-muted)]">
          The sample is exhausted — everything it would have offered has been read.
        </p>
      )}
    </section>
  );
}
